/**
 * Bereich *Geld* (Spec 04a, „Oberfläche · Reiter Offene Posten"; Spec 04c,
 * „Die Reiterleiste im Bereich Geld"). Drei Reiter: *Offene Posten* (04a/04b),
 * *Zahlungsmatrix* (Spec 08, `GET /billing/matrix`) und *Klärfälle* (04c,
 * `GET /billing/unallocated`) — die Reiterleiste (`scope-geld-tabs`) entstand
 * mit zwei Einträgen (Spec 04c: „ein leerer Reiter würde etwas versprechen,
 * das nicht da ist"), Spec 08 hängt den dritten an **zweiter** Stelle an
 * (AK-483).
 *
 * Zustand steht in der URL (`?overdue=&tenant=&scope=&tab=&von=`), ein
 * geteilter Link zeigt beim Empfänger dieselbe Ansicht (AK-296, AK-322,
 * AK-484). Die Filter des Reiters *Offene Posten* und `von` (Spec 08) bleiben
 * beim Wechseln des Reiters in der URL erhalten — der Reiter-Knopf ändert per
 * `navigate()`-Merge nur `tab`, nie die übrigen Parameter. Der Filter
 * „Objektbaum" braucht `features/objects` — diese Route darf mehrere
 * `features` zusammensetzen (anders als `features/billing`, das
 * `features/objects` nicht kennen darf), deshalb sitzt `ScopeFilterField` hier
 * lokal statt in `features/billing`.
 *
 * Spec 04b ergänzt die Schnellaktion `open-item-pay-<id>` je Zeile
 * (AK-287/288/289) — sie öffnet `PaymentSheet` vorbelegt mit dem
 * angeklickten Posten. Der Zeilenklick selbst bleibt Navigation (04b fügt
 * hinzu, ändert nicht — AK-318 aus 04a bleibt grün). Spec 04c ergänzt den
 * dritten Sheet-Einstieg: eine Zeile in *Klärfälle* öffnet dasselbe Sheet mit
 * `unallocatedEntry` statt `prefill` (siehe `PaymentSheet.tsx`). Spec 08
 * ergänzt den vierten: eine Kachel der Zahlungsmatrix füllt `paymentPrefill`
 * genauso wie eine Zeile offener Posten — **dasselbe** Sheet, kein zweites
 * (Fachregel 5 der Spec). Spec 07 ergänzt den fünften und letzten:
 * `payment-add` im Kopf öffnet dasselbe Sheet ganz ohne Vorbelegung — bis
 * dahin unter anderem Bezeichner auf dem inzwischen entfallenen Bildschirm
 * „Heute" (AK-456).
 */

import { useNavigate, useSearch } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import { useState } from 'react'

import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import type { OpenItem, UnallocatedPayment } from '@/features/billing/api'
import { useMatrix, useOpenItems, usePrepaid, useUnallocated } from '@/features/billing/api'
import { OpenItemsList } from '@/features/billing/OpenItemsList'
import { PaymentMatrix } from '@/features/billing/PaymentMatrix'
import { PrepaidList } from '@/features/billing/PrepaidList'
import { PaymentSheet } from '@/features/billing/PaymentSheet'
import type { PaymentSheetPrefill, PaymentSheetUnallocatedEntry } from '@/features/billing/PaymentSheet'
import { TenantFilterField } from '@/features/billing/TenantFilterField'
import type { TenantFilterValue } from '@/features/billing/TenantFilterField'
import { UnallocatedList } from '@/features/billing/UnallocatedList'
import { useEntitiesTree, useEntityHead } from '@/features/objects/api'
import type { TreeNode } from '@/features/objects/api'
import { FIELD_CLASS } from '@/components/FormField'
import { useTenantDetail } from '@/features/tenants/api'

const GELD_TABS = [
  { id: 'offene-posten', label: 'Offene Posten' },
  { id: 'matrix', label: 'Zahlungsmatrix' },
  { id: 'klaerfaelle', label: 'Klärfälle' },
] as const

export function Geld() {
  const navigate = useNavigate({ from: '/geld' })
  const search = useSearch({ from: '/geld' })

  // `tenantId`/`scopeId` fehlen im Filterobjekt ganz, wenn kein Wert gewählt
  // ist — mit `exactOptionalPropertyTypes` ist ein ausdrückliches `undefined`
  // etwas anderes als ein fehlender Schlüssel.
  const openItemsQuery = useOpenItems(
    {
      overdue: search.overdue,
      ...(search.tenant !== undefined ? { tenantId: search.tenant } : {}),
      ...(search.scope !== undefined ? { scopeId: search.scope } : {}),
    },
    { enabled: search.tab === 'offene-posten' },
  )
  // Reiter *Klärfälle* (Spec 04c) — ungefiltert, dieselbe Liste für jeden
  // Aufrufer; ein Filter „Mieter"/„Objekt" ist für diesen Reiter nicht
  // vorgesehen (die Spec nennt für `unallocated-list` keinen).
  const unallocatedQuery = useUnallocated({}, { enabled: search.tab === 'klaerfaelle' })
  // Reiter *Zahlungsmatrix* (Spec 08) — kein Filter (die Spec schließt
  // „Filter nach Mieter oder Objektbaum" ausdrücklich aus, „Nicht
  // enthalten"); `von` ist der einzige Zustand, den dieser Reiter aus der URL
  // liest. Fehlt er, liefert der Server sein eigenes Fenster (endet im
  // laufenden Monat) — der Client bildet dafür kein eigenes Zwischenformat.
  const matrixFrom = search.von !== undefined ? `${search.von}-01` : undefined
  const matrixQuery = useMatrix(matrixFrom, { enabled: search.tab === 'matrix' })
  const prepaidQuery = usePrepaid(
    {
      ...(search.tenant !== undefined ? { tenantId: search.tenant } : {}),
      ...(search.scope !== undefined ? { scopeId: search.scope } : {}),
    },
    { enabled: search.tab === 'offene-posten' },
  )

  const hasActiveFilter = search.overdue || search.tenant !== undefined || search.scope !== undefined
  const [tenantLabel, setTenantLabel] = useState<string | undefined>(undefined)
  const [scopeLabel, setScopeLabel] = useState<string | undefined>(undefined)

  // Zahlung erfassen aus der Schnellaktion einer Zeile (Spec 04b) oder
  // Zuordnung ergänzen aus den Klärfällen (Spec 04c) — dasselbe Sheet, zwei
  // sich ausschließende Vorbelegungen (siehe `PaymentSheet.tsx`).
  const [paymentPrefill, setPaymentPrefill] = useState<PaymentSheetPrefill | null>(null)
  const [unallocatedEntry, setUnallocatedEntry] = useState<PaymentSheetUnallocatedEntry | null>(null)
  const [isPaymentSheetOpen, setIsPaymentSheetOpen] = useState(false)
  const [confirmation, setConfirmation] = useState<string | null>(null)

  // Öffnet das Sheet mit einer fertigen Vorbelegung — von der Schnellaktion
  // einer Zeile offener Posten (Spec 04b, `openPaymentSheet` unten) ebenso
  // wie von einem Klick auf eine Kachel der Zahlungsmatrix mit offenem Rest
  // (Spec 08, Fachregel 5). Letztere hat `PaymentSheetPrefill` bereits fertig
  // gebaut (`chargeId` gesetzt oder `null`, je nachdem ob die Kachel genau
  // eine offene Sollstellung bündelt, AK-478) und wird deshalb direkt als
  // `onPay` durchgereicht, ohne eigenen Zwischenschritt.
  function openPaymentSheetWithPrefill(prefill: PaymentSheetPrefill) {
    setUnallocatedEntry(null)
    setPaymentPrefill(prefill)
    setIsPaymentSheetOpen(true)
  }

  function openPaymentSheet(item: OpenItem) {
    openPaymentSheetWithPrefill({
      tenantId: item.tenant.id,
      tenantLabel: item.tenant.label,
      chargeId: item.charge_id,
      openCents: item.open_cents,
    })
  }

  // Einstieg „ohne Bezug" (`payment-add`, Spec 04b) — bis Spec 07 lebte er
  // unter einem anderen Bezeichner auf „Heute"; der Bildschirm ist mit dem
  // Kalender entfallen, die eine primäre Aktion zieht in den Bereich, der sie
  // inhaltlich betrifft (AK-456). Mieter wird im Sheet gewählt, wie bisher.
  function openPaymentSheetWithoutPrefill() {
    setUnallocatedEntry(null)
    setPaymentPrefill(null)
    setIsPaymentSheetOpen(true)
  }

  function openUnallocatedSheet(item: UnallocatedPayment) {
    setPaymentPrefill(null)
    setUnallocatedEntry({
      paymentId: item.id,
      tenantId: item.tenant.id,
      tenantLabel: item.tenant.label,
      amountDisplay: item.amount_display,
      receivedOn: item.received_on,
      methodLabel: item.method.label,
      reference: item.reference,
      unallocatedCents: item.unallocated_cents,
    })
    setIsPaymentSheetOpen(true)
  }

  // Beim Öffnen eines geteilten Links (AK-296) steht in der URL nur die ID —
  // der Name kommt aus derselben Detailabfrage, die auch der Bildschirm
  // *Mieter* bzw. *Objekt* benutzt. `enabled` greift nur, solange der Name
  // nicht schon aus einer Auswahl in dieser Sitzung bekannt ist. Vorher
  // stand hier ersatzweise die rohe ID (KD1).
  const tenantDetailQuery = useTenantDetail(search.tenant ?? '', {
    enabled: search.tenant !== undefined && tenantLabel === undefined,
  })
  const scopeHeadQuery = useEntityHead(search.scope ?? '', {
    enabled: search.scope !== undefined && scopeLabel === undefined,
  })
  const resolvedTenantLabel = tenantLabel ?? tenantDetailQuery.data?.display_name
  const resolvedScopeLabel = scopeLabel ?? scopeHeadQuery.data?.label

  function resetFilters() {
    setTenantLabel(undefined)
    setScopeLabel(undefined)
    // `tab` bleibt erhalten — der Knopf sitzt nur im Reiter *Offene Posten*
    // (unten), ein voller Literal-Ersatz ohne `tab` würde ihn sonst auf den
    // Standardreiter zurückwerfen (`toGeldTabId`, `router.tsx`).
    void navigate({ search: { overdue: false, tenant: undefined, scope: undefined, tab: search.tab } })
  }

  return (
    <section aria-labelledby="geld-heading" data-testid="scope-geld" className="flex h-full flex-col">
      {/* Der Kopf trägt die **Ansicht**, nicht den Bereich.
       *
       * „Geld" steht seit ADR 0011 in der Kontext-Sidebar daneben; hier ein
       * zweites Mal, hieße dasselbe Wort zweimal auf 30 cm — und die Zeile,
       * die am meisten Platz hat, sagte am wenigsten. Sie beantwortet jetzt
       * die Frage, die man vor der Liste hat: *welchen Ausschnitt sehe ich
       * gerade?* (Befund aus S3, `tasks/todo-web-schale-b.md`.)
       *
       * Der Name folgt dem Zustand aus der URL, nicht einem eigenen State —
       * ein geteilter Link zeigt beim Empfänger denselben Titel. */}
      <header className="border-border flex h-(--row-h) items-center justify-between gap-3 border-b px-(--page-x)">
        <h1 id="geld-heading" className="text-page font-(--font-weight-page)">
          {viewTitle(search.tab, search.overdue)}
        </h1>
        {/* Die eine primäre Aktion des Bereichs (`design-system`: „nie mehr
         * als eine primäre Aktion pro Ansicht") — unabhängig vom gewählten
         * Reiter sichtbar, wie zuvor auf „Heute" (Spec 04b). */}
        <button
          type="button"
          data-testid="payment-add"
          onClick={openPaymentSheetWithoutPrefill}
          className="bg-accent rounded-card text-body shrink-0 px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
        >
          Zahlung erfassen
        </button>
      </header>

      {/* Die Reiterleiste (Spec 04c, „Die Reiterleiste im Bereich Geld") —
       * optisch dieselbe Gestaltung wie `object-tabs` (`routes/ObjectDetail.tsx`),
       * für dasselbe Wiedererkennen über den Bereich hinweg. Ein `<button>` mit
       * `navigate()` statt `<Link>`: Dieselbe Route braucht einen **Merge** der
       * Suchparameter (nur `tab` ändert sich, die Filter des Reiters *Offene
       * Posten* bleiben erhalten, Spec 04c) — genau das Muster, das diese Datei
       * schon für `open-items-filter-overdue` benutzt, statt ein zweites gegen
       * TanStack Routers Typinferenz für `<Link search={fn}>` zu erkämpfen. */}
      <nav aria-label="Reiter Geld" data-testid="scope-geld-tabs">
        <ul className="border-border flex overflow-x-auto border-b px-(--page-x)">
          {GELD_TABS.map((entry) => {
            const isActive = entry.id === search.tab
            return (
              <li key={entry.id}>
                <button
                  type="button"
                  onClick={() => {
                    void navigate({ search: (prev) => ({ ...prev, tab: entry.id }) })
                  }}
                  data-testid={`scope-geld-tab-${entry.id}`}
                  aria-current={isActive ? 'page' : undefined}
                  className={`text-body flex items-center border-b-2 px-3 whitespace-nowrap transition-colors duration-(--duration-fast) ease-(--ease-out) ${
                    isActive ? 'border-accent text-text' : 'text-muted hover:text-text border-transparent'
                  }`}
                >
                  {entry.label}
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      {search.tab === 'offene-posten' && (
        <>
          <div className="border-border flex flex-wrap items-center gap-2 border-b px-(--page-x) py-2">
            <button
              type="button"
              role="switch"
              aria-checked={search.overdue}
              data-testid="open-items-filter-overdue"
              onClick={() => {
                void navigate({ search: (prev) => ({ ...prev, overdue: !prev.overdue }) })
              }}
              className={`rounded-card text-body flex min-h-(--tap-min) items-center gap-2 border px-3 transition-colors duration-(--duration-fast) ease-(--ease-out) ${
                search.overdue ? 'border-accent text-accent bg-accent/10' : 'border-border text-text hover:bg-bg'
              }`}
            >
              nur überfällige
            </button>

            <TenantFilterField
              value={
                search.tenant === undefined
                  ? null
                  : {
                      id: search.tenant,
                      label: resolvedTenantLabel ?? '',
                      isResolving: resolvedTenantLabel === undefined,
                    }
              }
              onSelect={(tenant: TenantFilterValue) => {
                setTenantLabel(tenant.label)
                void navigate({ search: (prev) => ({ ...prev, tenant: tenant.id }) })
              }}
              onClear={() => {
                setTenantLabel(undefined)
                void navigate({ search: (prev) => ({ ...prev, tenant: undefined }) })
              }}
            />

            <ScopeFilterField
              value={
                search.scope === undefined
                  ? null
                  : {
                      id: search.scope,
                      label: resolvedScopeLabel ?? '',
                      isResolving: resolvedScopeLabel === undefined,
                    }
              }
              onSelect={(scope) => {
                setScopeLabel(scope.label)
                void navigate({ search: (prev) => ({ ...prev, scope: scope.id }) })
              }}
              onClear={() => {
                setScopeLabel(undefined)
                void navigate({ search: (prev) => ({ ...prev, scope: undefined }) })
              }}
            />

            {hasActiveFilter && (
              <button
                type="button"
                data-testid="open-items-filter-reset"
                onClick={resetFilters}
                className="text-accent text-label hover:underline"
              >
                Filter zurücksetzen
              </button>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <OpenItemsList
              query={openItemsQuery}
              listTestId="open-items"
              loadingTestId="open-items-loading"
              errorTestId="open-items-error"
              retryTestId="open-items-retry"
              emptyTestId="open-items-empty"
              emptyTitle={hasActiveFilter ? 'Kein Posten entspricht dem Filter.' : 'Keine offenen Posten.'}
              emptyHint={hasActiveFilter ? 'Filter zurücksetzen.' : 'Alles ausgeglichen.'}
              rowTestId={(id) => `open-item-row-${id}`}
              overdueTestId={(id) => `open-item-overdue-${id}`}
              arrearsTestId={(id) => `open-item-arrears-${id}`}
              stickyTotals={false}
              onPay={openPaymentSheet}
              onOpenContract={(contractId) => {
                void navigate({ search: (previous) => ({ ...previous, contract: contractId }) })
              }}
            />
            <PrepaidList query={prepaidQuery} />
          </div>
        </>
      )}

      {search.tab === 'matrix' && (
        <PaymentMatrix
          query={matrixQuery}
          onNavigate={(von) => {
            void navigate({ search: (prev) => ({ ...prev, von }) })
          }}
          onPay={openPaymentSheetWithPrefill}
        />
      )}

      {search.tab === 'klaerfaelle' && (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <UnallocatedList query={unallocatedQuery} onSelect={openUnallocatedSheet} />
        </div>
      )}

      <PaymentSheet
        open={isPaymentSheetOpen}
        onOpenChange={setIsPaymentSheetOpen}
        prefill={paymentPrefill}
        unallocatedEntry={unallocatedEntry}
        onSaved={setConfirmation}
      />
      <ConfirmationStrip
        message={confirmation}
        onDismiss={() => {
          setConfirmation(null)
        }}
        testId="payment-confirmation"
      />
    </section>
  )
}

interface ScopeFilterValue {
  readonly id: string
  readonly label: string
  /** `true`, solange der Name aus der URL noch nicht bekannt ist — direkt
   * nach dem Öffnen eines geteilten Links (AK-296) steht nur die ID im
   * State, der Name kommt erst mit der Nachladeabfrage. Zeigt einen
   * Platzhalter statt der rohen ID (KD1). */
  readonly isResolving?: boolean
}

/** Filter „Objektbaum" (`open-items-filter-scope`) — dieselbe Datenquelle
 * wie `ObjectTree`, hier als Auswahl statt als Navigation: ein Klick auf
 * einen Knoten wählt ihn als Filter, statt zum Objekt zu springen. */
function ScopeFilterField({
  value,
  onSelect,
  onClear,
}: {
  readonly value: ScopeFilterValue | null
  readonly onSelect: (scope: ScopeFilterValue) => void
  readonly onClear: () => void
}) {
  const [isOpen, setIsOpen] = useState(false)

  if (value !== null) {
    return (
      <div data-testid="open-items-filter-scope" className={`flex items-center gap-2 ${FIELD_CLASS}`}>
        {value.isResolving === true ? (
          <span aria-busy="true" aria-live="polite" className="flex h-4 items-center">
            <span className="sr-only">Wird geladen</span>
            <span aria-hidden="true" className="bg-border h-2 w-20 animate-pulse rounded-full" />
          </span>
        ) : (
          <span className="text-body text-text">{value.label}</span>
        )}
        <button
          type="button"
          data-testid="open-items-filter-scope-clear"
          aria-label="Objektfilter entfernen"
          onClick={onClear}
          className="text-muted hover:text-text flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center"
        >
          <X className="size-3.5" />
        </button>
      </div>
    )
  }

  return (
    <div data-testid="open-items-filter-scope" className="relative">
      <button
        type="button"
        onClick={() => {
          setIsOpen((current) => !current)
        }}
        className={`text-text px-3 ${FIELD_CLASS}`}
      >
        Objekt wählen
      </button>
      {isOpen && (
        <div className="border-border bg-surface rounded-card absolute top-full z-10 mt-1 max-h-72 w-64 overflow-y-auto border">
          <ScopeTree
            onSelect={(node) => {
              onSelect({ id: node.id, label: node.label })
              setIsOpen(false)
            }}
          />
        </div>
      )}
    </div>
  )
}

function ScopeTree({ onSelect }: { readonly onSelect: (node: TreeNode) => void }) {
  const query = useEntitiesTree(undefined)

  if (query.isPending) return <SkeletonRows testId="open-items-filter-scope-loading" rows={3} />
  if (query.isError) {
    return (
      <ErrorState
        testId="open-items-filter-scope-error"
        retryTestId="open-items-filter-scope-retry"
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  }
  if (query.data.items.length === 0) {
    return <EmptyState testId="open-items-filter-scope-empty" title="Noch kein Grundstück angelegt." />
  }

  return (
    <ul>
      {query.data.items.map((node) => (
        <ScopeTreeRow key={node.id} node={node} depth={0} onSelect={onSelect} />
      ))}
    </ul>
  )
}

function ScopeTreeRow({
  node,
  depth,
  onSelect,
}: {
  readonly node: TreeNode
  readonly depth: number
  readonly onSelect: (node: TreeNode) => void
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const childrenQuery = useEntitiesTree(node.id, { enabled: isExpanded })

  return (
    <li>
      <div className="flex items-center" style={{ paddingLeft: `${String(depth * 16)}px` }}>
        {node.has_children ? (
          <button
            type="button"
            data-testid={`open-items-filter-scope-toggle-${node.id}`}
            aria-label={isExpanded ? `${node.label} einklappen` : `${node.label} aufklappen`}
            aria-expanded={isExpanded}
            onClick={() => {
              setIsExpanded((current) => !current)
            }}
            className="text-muted hover:text-text flex h-(--row-h) w-(--row-h) shrink-0 items-center justify-center transition-colors duration-(--duration-fast) ease-(--ease-out)"
          >
            {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
        ) : (
          <span aria-hidden="true" className="h-(--row-h) w-(--row-h) shrink-0" />
        )}
        <button
          type="button"
          data-testid={`open-items-filter-scope-option-${node.id}`}
          onClick={() => {
            onSelect(node)
          }}
          className="hover:bg-bg flex min-h-(--tap-min) flex-1 items-center px-1 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
        >
          <span className="text-body text-text">{node.label}</span>
        </button>
      </div>

      {isExpanded && childrenQuery.isSuccess && (
        <ul>
          {childrenQuery.data.items.map((child) => (
            <ScopeTreeRow key={child.id} node={child} depth={depth + 1} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * Der Name der gezeigten Ansicht — dieselben Wörter wie in der Sidebar
 * (`sidebar-geld-*`, `router.tsx`) und auf der Reiterleiste. Eine Ansicht
 * behält ihren Namen über die ganze Oberfläche hinweg (`design-system`,
 * „Eine Aktion behält ihren Namen"); zwei Namen für denselben Ausschnitt
 * wären zwei Ausschnitte in den Augen des Lesers.
 */
function viewTitle(tab: 'offene-posten' | 'matrix' | 'klaerfaelle', overdue: boolean): string {
  if (tab === 'klaerfaelle') return 'Klärfälle'
  if (tab === 'matrix') return 'Zahlungsmatrix'
  return overdue ? 'Überfällig' : 'Alle offenen'
}
